<script setup>
import { cva } from 'class-variance-authority';
import { computed } from 'vue';
import { ADVERT_TYPE_BTR, ADVERT_TYPE_SPARE_ROOM } from '@js/constants';
import PropertyTypeIcon from '@components/Property/SVG/PropertyTypeIcon.vue';
import { VARIANT_REMAINING_ROOMS } from '@components/Property/livingContext.ts';

const PROPERTY_TYPE_APARTMENT = 'apartment';

const props = defineProps({
  advertType: {
    type: String,
    required: false,
    default: null,
  },
  propertyType: {
    type: String,
    required: false,
    default: null,
  },
  spareRoomsAvailableCount: {
    type: Number,
    required: false,
    default: 0,
  },
  variantKind: {
    type: String,
    required: false,
    default: null,
  },
  roomsAvailableCount: {
    type: Number,
    required: false,
    default: 0,
  },
});

const variant = computed(() => {
  // A blended result is described by the availability variant that matched, so a partially
  // occupied house reads the same as a spare room advert
  if (props.variantKind === VARIANT_REMAINING_ROOMS) {
    return {
      label:
        props.roomsAvailableCount === 1 ? 'Room available' : 'Rooms available',
      theme: 'orange',
      iconType: 'spare-rooms',
    };
  }

  if (props.advertType === ADVERT_TYPE_BTR) {
    return {
      label: 'Private halls',
      theme: 'blue',
      iconType: 'private-halls',
    };
  }

  if (props.advertType === ADVERT_TYPE_SPARE_ROOM) {
    return {
      label:
        props.spareRoomsAvailableCount === 1
          ? 'Room available'
          : 'Rooms available',
      theme: 'orange',
      iconType: 'spare-rooms',
    };
  }

  if (props.propertyType === PROPERTY_TYPE_APARTMENT) {
    return {
      label: 'Apartment',
      theme: 'blue',
      iconType: 'apartment',
    };
  }

  return {
    label: 'House',
    theme: 'blue',
    iconType: 'house',
  };
});

const classes = cva(
  'tw-pointer-events-none tw-inline-flex tw-items-center tw-gap-[6px] tw-px-[8px] tw-py-[4px] tw-text-[12px] tw-font-bold tw-leading-[18px] tw-shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]',
  {
    variants: {
      theme: {
        blue: 'tw-rounded-[16px] tw-bg-uhblue-50 tw-text-uhblue-700',
        orange:
          'tw-rounded-[100px] tw-bg-[#FDEDDF] tw-text-refreshPrimaryBg-darker',
      },
    },
  }
);
</script>

<template>
  <div
    :class="classes({ theme: variant.theme })"
    data-uh="property-type-pill"
    :data-theme="variant.theme"
  >
    <PropertyTypeIcon :type="variant.iconType" />
    <span>{{ variant.label }}</span>
  </div>
</template>
